/**
 * RunAllProgressPanel — in-page aggregate stage list for the M3 Run-all
 * chain (FP-4: not a modal). Each chained stage renders its own state;
 * review checkpoints appear as explicit paused rows with a primary review
 * action. Full error text is always visible (LT2 rule). Stopping only stops
 * the client-side chain — dispatched backend tasks keep running.
 */

import React from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { CONTENT_PLAN } from '@test-kit/selectors/content-plan';
import type { RunAllStageState } from './useRunAllOrchestrator';

export interface RunAllStageAction {
  label: string;
  onClick: () => void;
  /** Secondary (text) action rendered next to the primary one. */
  secondary?: boolean;
}

export interface RunAllProgressPanelProps {
  stages: RunAllStageState[];
  active: boolean;
  /** Live phase of the currently running backend task (FP-11 honest phases). */
  currentPhaseName?: string;
  /** Paused/errored row actions, resolved by the strip per pauseKind. */
  actionsForStage: (stage: RunAllStageState) => RunAllStageAction[];
  onStop: () => void;
  onDismiss: () => void;
}

function stageIcon(stage: RunAllStageState) {
  switch (stage.status) {
    case 'done':
      return <CheckCircleOutlineIcon fontSize="small" color="success" />;
    case 'skipped':
      return <SkipNextIcon fontSize="small" color="disabled" />;
    case 'running':
      return <CircularProgress size={16} thickness={5} />;
    case 'paused':
      return <PauseCircleOutlineIcon fontSize="small" color="warning" />;
    case 'error':
      return <ErrorOutlineIcon fontSize="small" color="error" />;
    default:
      return <RadioButtonUncheckedIcon fontSize="small" color="disabled" />;
  }
}

const STATUS_LABEL: Record<RunAllStageState['status'], string> = {
  pending: 'Pending',
  running: 'Running',
  paused: 'Waiting for review',
  skipped: 'Skipped',
  done: 'Done',
  error: 'Failed',
};

/** Internal chain metadata is carried in `note` with the `chained:` prefix. */
function visibleNote(stage: RunAllStageState): string | undefined {
  if (!stage.note) return undefined;
  if (stage.note.startsWith('chained:')) return 'Strategy created.';
  return stage.note;
}

export const RunAllProgressPanel: React.FC<RunAllProgressPanelProps> = ({
  stages, active, currentPhaseName, actionsForStage, onStop, onDismiss,
}) => {
  const finished = stages.every((s) => s.status === 'done' || s.status === 'skipped');

  return (
    <Paper variant="outlined" data-testid={CONTENT_PLAN.RUNALL_PANEL} sx={{ p: 1.5, mt: 1 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          Run all — staged progress
        </Typography>
        {active && !finished && (
          <Button
            size="small"
            color="inherit"
            onClick={onStop}
            data-testid={CONTENT_PLAN.RUNALL_STOP}
          >
            Stop
          </Button>
        )}
        {(!active || finished) && (
          <Button
            size="small"
            color="inherit"
            onClick={onDismiss}
            data-testid={CONTENT_PLAN.RUNALL_DISMISS}
          >
            Dismiss
          </Button>
        )}
      </Stack>

      {finished && (
        <Alert severity="success" sx={{ mb: 1 }}>
          Run-all chain finished — every stage is done or already satisfied.
        </Alert>
      )}
      {!active && !finished && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Chain stopped. Backend tasks that were already dispatched keep
          running; press a stage Retry (or Run all again) to resume — completed
          stages are skipped.
        </Alert>
      )}

      <Stack spacing={0.75}>
        {stages.map((stage) => {
          const actions = actionsForStage(stage);
          return (
            <Box
              key={stage.id}
              data-testid={CONTENT_PLAN.runAllStageRow(stage.id)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                p: 1,
                borderRadius: 1,
                bgcolor: stage.status === 'running' || stage.status === 'paused'
                  ? 'action.hover'
                  : undefined,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                {stageIcon(stage)}
                <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 130 }}>
                  {stage.label}
                </Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  color={
                    stage.status === 'error' ? 'error'
                      : stage.status === 'paused' ? 'warning'
                        : stage.status === 'done' ? 'success'
                          : 'default'
                  }
                  label={
                    stage.status === 'running' && currentPhaseName
                      ? currentPhaseName
                      : STATUS_LABEL[stage.status]
                  }
                />
                <Box sx={{ flex: 1 }} />
                {actions.map((action) => (
                  <Button
                    key={action.label}
                    size="small"
                    variant={action.secondary ? 'text' : 'outlined'}
                    onClick={action.onClick}
                    data-testid={action.secondary
                      ? CONTENT_PLAN.RUNALL_STAGE_SECONDARY
                      : CONTENT_PLAN.RUNALL_STAGE_ACTION}
                  >
                    {action.label}
                  </Button>
                ))}
              </Stack>
              {visibleNote(stage) && stage.status !== 'error' && (
                <Typography variant="caption" color="text.secondary" sx={{ pl: 3.5 }}>
                  {visibleNote(stage)}
                </Typography>
              )}
              {stage.status === 'error' && stage.error && (
                <Alert severity="error" sx={{ ml: 3.5 }}>
                  {/* Full error text — persistently visible, never hover-only. */}
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {stage.error}
                  </Typography>
                </Alert>
              )}
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
};

export default RunAllProgressPanel;
